import type { DataStore } from '../data/store';
import type { Id, IsoDate, Manifest, YearMonth } from '../data/types';
import { parseGlobalFilters, type KnownIds } from './url-state';

export type RouteId = 'home' | 'net-worth' | 'accounts' | 'expenses' | 'income' | 'transactions';
export type FilterMode = 'stock' | 'flow' | 'transactions';

export const ROUTES: ReadonlyArray<{ id: RouteId; path: string; label: string; icon: string }> = [
  { id: 'home', path: '/', label: 'Home', icon: 'home' },
  { id: 'net-worth', path: '/net-worth', label: 'Net worth', icon: 'trend' },
  { id: 'accounts', path: '/accounts', label: 'Account', icon: 'wallet' },
  { id: 'expenses', path: '/expenses', label: 'Spese', icon: 'arrow-down' },
  { id: 'income', path: '/income', label: 'Entrate', icon: 'arrow-up' },
  { id: 'transactions', path: '/transactions', label: 'Transazioni', icon: 'list' },
];

function routeFromPath(pathname: string): RouteId {
  return ROUTES.find((route) => route.path === pathname)?.id ?? 'home';
}

function routePath(routeId: RouteId): string {
  return ROUTES.find((route) => route.id === routeId)?.path ?? '/';
}

function routeSupportsCategories(routeId: RouteId): boolean {
  return (
    routeId === 'accounts' ||
    routeId === 'expenses' ||
    routeId === 'income' ||
    routeId === 'transactions'
  );
}

export class AppState {
  route = $state<RouteId>(routeFromPath(window.location.pathname));
  fromMonth = $state<YearMonth | null>(null);
  toMonth = $state<YearMonth | null>(null);
  fromDate = $state<IsoDate | null>(null);
  toDate = $state<IsoDate | null>(null);
  minMonth = $state<YearMonth | null>(null);
  maxMonth = $state<YearMonth | null>(null);
  minDate = $state<IsoDate | null>(null);
  maxDate = $state<IsoDate | null>(null);
  accountIds = $state<Id[]>([]);
  categoryIds = $state<Id[]>([]);
  accountFilterActive = $state(false);
  categoryFilterActive = $state(false);
  includeUncategorized = $state(false);
  filterSheetOpen = $state(false);
  dark = $state(localStorage.getItem('money-theme') === 'dark');
  /** Incrementato a ogni `applyLocation`: le pagine con stato locale da URL si rimontano. */
  historyVersion = $state(0);
  #known: KnownIds | undefined;

  get filterMode(): FilterMode {
    if (this.route === 'transactions') return 'transactions';
    if (this.route === 'expenses' || this.route === 'income') return 'flow';
    return 'stock';
  }

  get categoryFiltersAvailable(): boolean {
    return routeSupportsCategories(this.route);
  }

  get activeFilterCount(): number {
    return (
      Number(this.periodFilterActive) +
      Number(this.accountFilterActive) +
      Number(this.categoryFiltersAvailable && this.categoryFilterActive)
    );
  }

  get periodFilterActive(): boolean {
    return this.filterMode === 'stock'
      ? this.fromMonth !== this.minMonth || this.toMonth !== this.maxMonth
      : this.fromDate !== this.minDate || this.toDate !== this.maxDate;
  }

  initialize(store: DataStore, range?: Manifest['range']): void {
    const firstMonth = store.months[0];
    const lastMonth = store.months.at(-1);
    const firstDate = range?.first_date ?? store.core.calendar[0]?.date;
    const lastDate = range?.last_date ?? store.core.calendar.at(-1)?.date;
    if (!firstMonth || !lastMonth || !firstDate || !lastDate) return;

    this.minMonth = firstMonth;
    this.maxMonth = lastMonth;
    this.minDate = firstDate;
    this.maxDate = lastDate;
    this.#known = {
      accountIds: new Set(store.accountById.keys()),
      categoryIds: new Set(store.categoryById.keys()),
    };
    this.applyLocation();
  }

  /**
   * Riallinea route e filtri all'URL corrente. Usato all'avvio e su `popstate`, così
   * Indietro/Avanti ripristinano esattamente lo stato che l'URL descrive.
   */
  applyLocation(location: Pick<Location, 'pathname' | 'search'> = window.location): void {
    this.route = routeFromPath(location.pathname);
    if (!this.minMonth || !this.maxMonth || !this.minDate || !this.maxDate || !this.#known) return;
    const parsed = parseGlobalFilters(
      new URLSearchParams(location.search),
      {
        minMonth: this.minMonth,
        maxMonth: this.maxMonth,
        minDate: this.minDate,
        maxDate: this.maxDate,
      },
      this.#known,
      routeSupportsCategories(this.route),
    );
    this.fromMonth = parsed.fromMonth;
    this.toMonth = parsed.toMonth;
    this.fromDate = parsed.fromDate;
    this.toDate = parsed.toDate;
    this.accountFilterActive = parsed.accountFilterActive;
    this.accountIds = parsed.accountIds;
    this.categoryFilterActive = parsed.categoryFilterActive;
    this.categoryIds = parsed.categoryIds;
    this.includeUncategorized = parsed.includeUncategorized;
    this.historyVersion += 1;
  }

  /** Stato serializzabile per la telemetria (`window.__money.snapshot().state`). */
  toJSON() {
    return {
      route: this.route,
      url: this.toUrl(this.route),
      filterMode: this.filterMode,
      period:
        this.filterMode === 'stock'
          ? { from: this.fromMonth, to: this.toMonth }
          : { from: this.fromDate, to: this.toDate },
      bounds: {
        minMonth: this.minMonth,
        maxMonth: this.maxMonth,
        minDate: this.minDate,
        maxDate: this.maxDate,
      },
      accounts: this.accountFilterActive ? [...this.accountIds] : 'all',
      categories: this.categoryFilterActive ? [...this.categoryIds] : 'all',
      includeUncategorized: this.includeUncategorized,
      activeFilterCount: this.activeFilterCount,
      theme: this.dark ? 'dark' : 'light',
      filterSheetOpen: this.filterSheetOpen,
    };
  }

  navigate(route: RouteId, replace = false): void {
    this.route = route;
    const url = this.toUrl(route);
    window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
    requestAnimationFrame(() => document.querySelector<HTMLElement>('main h1')?.focus());
  }

  syncUrl(): void {
    window.history.replaceState({}, '', this.toUrl(this.route));
  }

  toUrl(route: RouteId): string {
    const params = new URLSearchParams();
    if (this.fromMonth) params.set('fromMonth', this.fromMonth);
    if (this.toMonth) params.set('toMonth', this.toMonth);
    if (this.fromDate) params.set('from', this.fromDate);
    if (this.toDate) params.set('to', this.toDate);
    if (this.accountFilterActive) params.set('accounts', this.accountIds.join(','));
    if (routeSupportsCategories(route) && this.categoryFilterActive)
      params.set('categories', this.categoryIds.join(','));
    if (routeSupportsCategories(route) && this.categoryFilterActive && this.includeUncategorized)
      params.set('uncategorized', '1');
    const query = params.toString();
    return `${routePath(route)}${query ? `?${query}` : ''}`;
  }

  toggleTheme(): void {
    this.dark = !this.dark;
    localStorage.setItem('money-theme', this.dark ? 'dark' : 'light');
    document.documentElement.dataset.theme = this.dark ? 'dark' : 'light';
    window.dispatchEvent(new CustomEvent('money-theme-change'));
  }

  resetFilters(): void {
    this.fromMonth = this.minMonth;
    this.toMonth = this.maxMonth;
    this.fromDate = this.minDate;
    this.toDate = this.maxDate;
    this.accountIds = [];
    this.categoryIds = [];
    this.accountFilterActive = false;
    this.categoryFilterActive = false;
    this.includeUncategorized = false;
    this.syncUrl();
  }
}
